import { KAMINO_EMIT_PF_IDS } from '../constants/oikosReceitas';
import powerappsApi from '../services/powerapps';
import oikosApi from '../services/oikos';
import qs from 'qs';

const powerappsMultipart = {
  headers: {
    'Content-Type': 'multipart/form-data',
    Accept: 'multipart/form-data',
  },
};

export function canEmitNfse(user) {
  return KAMINO_EMIT_PF_IDS.includes(Number(user?.pf_id));
}

/**
 * @returns {'nao_enviado'|'aguardando'|'sincronizado'}
 */
export function getKaminoEmitStatus(rec) {
  const kaminoId = Number(rec?.kamino_id) || 0;
  const nfId = Number(rec?.nf_id) || 0;
  const fat = Number(rec?.rec_fat) === 1;
  if (nfId > 0 || fat) return 'sincronizado';
  if (kaminoId > 0) return 'aguardando';
  return 'nao_enviado';
}

export function kaminoStatusLabel(status) {
  if (status === 'aguardando') return 'Aguardando emissão';
  if (status === 'sincronizado') return 'Sincronizada';
  return 'Não enviado';
}

/**
 * Lista o que impede o envio (campos vazios, PJ sem kamino_id, gates de negócio).
 * @returns {string[]}
 */
export function getEmitMissingFields(rec) {
  const missing = [];
  if (Number(rec?.rec_aut) !== 1) missing.push('Faturamento não liberado');
  if (Number(rec?.rec_fat) === 1) missing.push('Receita já faturada');
  if (!String(rec?.rec_NFDescricao || '').trim()) missing.push('Descrição da NF');
  if (rec?.kamino_imposto == null || String(rec.kamino_imposto).trim() === '') {
    missing.push('Tabela de imposto');
  }
  if (!String(rec?.contrato_codTrib || '').trim()) missing.push('Código de serviço');
  if (!(Number(rec?.mun_id) > 0)) missing.push('Município');
  if (!rec?.pjTomadorKaminoId) missing.push('Tomador sem ID Kamino');
  if (!rec?.pjPrestadorKaminoId) missing.push('Prestador sem ID Kamino');
  return missing;
}

/** Opções padrão de lista de serviços (IDs já usados no Oikos). */
export const DEFAULT_COD_TRIB_OPTIONS = [
  { value: '170901', label: '170901' },
  { value: '170101', label: '170101' },
];

/**
 * Lista tabelas de imposto ativas na Kamino via PowerApps API.
 * @returns {Promise<Array<{ value: string|number, label: string, raw: object }>>}
 */
export async function listarTabelasImposto() {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_listar_tabelas_imposto' },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'multipart/form-data',
      },
    },
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true || !Array.isArray(payload.data)) {
    throw new Error(payload?.message || 'Falha ao listar tabelas de imposto na Kamino.');
  }
  return payload.data
    .filter((row) => row && (row.Ativo === true || row.Ativo === 1 || row.Ativo == null))
    .map((row) => ({
      value: String(row.ID),
      label: `${row.ID} — ${row.Nome || 'Sem nome'}`,
      raw: row,
    }));
}

/**
 * Última receita emitida (ou enviada à Kamino) do mesmo orcRubr/projeto,
 * para reaproveitar campos de emissão.
 */
export async function fetchLastNfseModelByOrcRubr(orcRubr_id, excludeRecId = null) {
  if (!(Number(orcRubr_id) > 0)) return null;

  const res = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'rec',
      data: {
        orcRubr_id: Number(orcRubr_id),
        rec_ativo: 1,
      },
      fields: {
        "(SELECT IFNULL(NULLIF(TRIM(pj.pj_razaosocial), ''), pj.pj_nomefanta) FROM pj WHERE pj.pj_id = rec.pj_tomador LIMIT 1)":
          'pjTomadorNome',
        '(SELECT pj.kamino_id FROM pj WHERE pj.pj_id = rec.pj_tomador LIMIT 1)': 'pjTomadorKaminoId',
        '(SELECT pj.mun_id FROM pj WHERE pj.pj_id = rec.pj_tomador LIMIT 1)': 'pjTomadorMunId',
        '(SELECT m.uf_IBGECod FROM mun m WHERE m.mun_id = COALESCE(rec.mun_id, (SELECT pj.mun_id FROM pj WHERE pj.pj_id = rec.pj_tomador LIMIT 1)) LIMIT 1)':
          'munUfIBGECod',
      },
    })}`,
  );
  const rows = Array.isArray(res?.data) ? res.data : [];

  const candidates = rows
    .filter((r) => Number(r.rec_id) !== Number(excludeRecId))
    .filter(
      (r) =>
        Number(r.nf_id) > 0 || Number(r.rec_fat) === 1 || Number(r.kamino_id) > 0,
    )
    .filter(
      (r) =>
        String(r.rec_NFDescricao || '').trim() ||
        (r.kamino_imposto != null && String(r.kamino_imposto).trim() !== '') ||
        String(r.contrato_codTrib || '').trim() ||
        Number(r.pj_tomador) > 0,
    )
    .sort((a, b) => {
      const da = String(a.rec_dtMod || a.rec_dtCria || '');
      const db = String(b.rec_dtMod || b.rec_dtCria || '');
      if (da !== db) return db.localeCompare(da);
      return Number(b.rec_id) - Number(a.rec_id);
    });

  return candidates[0] || null;
}

export async function emitNfse({ rec_id }) {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_emit_nfse', rec_id: Number(rec_id) },
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true) {
    const msg = payload?.message || 'Falha ao emitir NFS-e.';
    const err = new Error(msg);
    err.payload = payload;
    throw err;
  }
  return payload;
}

export async function checkPendingNfse({ limit = 50 } = {}) {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_check_nfse', limit: Number(limit) || 50 },
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || typeof payload !== 'object') {
    return { processed: 0, skipped: 0, imported: [], errors: [] };
  }
  if (payload.ok === false) {
    throw new Error(payload.message || 'Falha ao sincronizar NFS-e Kamino.');
  }
  return payload;
}
